import { createContext, ReactNode, useCallback, useContext, useEffect, useState } from 'react';
import { api, getToken, setToken, setUnauthorizedHandler } from '@/lib/api';
import type { SessionInfo } from '@/lib/types';
import { useQueryClient } from '@tanstack/react-query';

interface AuthState {
  user: SessionInfo | null;
  loading: boolean;
  login: (company: string, username: string, password: string) => Promise<void>;
  logout: () => Promise<void>;
}

const Ctx = createContext<AuthState | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<SessionInfo | null>(null);
  const [loading, setLoading] = useState(true);
  const qc = useQueryClient();

  const clear = useCallback(() => {
    setToken(null);
    setUser(null);
    qc.clear();
  }, [qc]);

  useEffect(() => {
    setUnauthorizedHandler(clear);
    if (!getToken()) {
      setLoading(false);
      return;
    }
    api<SessionInfo>('/auth/me').then(setUser).catch(clear).finally(() => setLoading(false));
  }, [clear]);

  const login = async (company: string, username: string, password: string) => {
    const r = await api<{ token: string }>('/auth/login', { body: { company, username, password } });
    setToken(r.token);
    try {
      localStorage.setItem('dolphin.lastLogin', JSON.stringify({ company, username }));
    } catch { /* */ }
    setUser(await api<SessionInfo>('/auth/me'));
  };

  const logout = async () => {
    try {
      await api('/auth/logout', { method: 'POST' });
    } finally {
      clear();
    }
  };

  return <Ctx.Provider value={{ user, loading, login, logout }}>{children}</Ctx.Provider>;
}

export function useAuth() {
  const c = useContext(Ctx);
  if (!c) throw new Error('AuthProvider eksik');
  return c;
}
